import { message } from "antd";
import { API_CONFIG, getMsLoginApiBase } from "../../../config/netConfig";

interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  message: string;
  errors?: string[];
}

interface PaginationParams {
  current?: number;
  pageSize?: number;
  name?: string;
  code?: string;
  status?: string;
}

interface CreateRoleParams {
  name: string;
  code: string;
  description?: string;
}

interface UpdateRoleParams {
  name?: string;
  code?: string;
  description?: string;
  isActive?: boolean;
}

interface AssignAdminsParams {
  adminIds: string[];
}

// 获取认证token (优先使用mslogin_token)
const getAuthToken = (): string | null => {
  return localStorage.getItem("mslogin_token") || localStorage.getItem("token") || sessionStorage.getItem("token");
};

// 通用请求函数（包含认证功能）
const request = async <T = any>(url: string, options: any = {}): Promise<ApiResponse<T>> => {
  try {
    const defaultRoot = `${API_CONFIG.BASE_URL}${API_CONFIG.API_PREFIX}`.replace(/\/$/, "");
    const msRoot = getMsLoginApiBase().replace(/\/$/, "");
    // /api/role/* 由 msLogin 提供；REACT_APP_API_URL 若指 admin 等会 404
    const root = url.startsWith("/role") ? msRoot : defaultRoot;
    const fullUrl = `${root}${url.startsWith("/") ? url : `/${url}`}`;
    // console.debug("发送API请求:", fullUrl, options);

    const token = getAuthToken();
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
      ...(options.headers as Record<string, string>),
    };

    // 如果有token，添加Authorization头
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }

    const response = await fetch(fullUrl, {
      method: "GET",
      mode: "cors",
      credentials: "include",
      headers,
      ...options,
    });

    // console.debug("API响应状态:", response.status, response.statusText);
    // console.debug("API响应头:", Object.fromEntries(response.headers.entries()));

    if (!response.ok) {
      // 尝试解析响应体中的 JSON 错误信息
      let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
      try {
        const errorText = await response.text();
        console.error("API错误响应:", errorText);
        // 尝试解析为 JSON
        try {
          const errorData = JSON.parse(errorText);
          // 如果响应体是标准格式 { success, message }，提取 message
          if (errorData && typeof errorData === "object") {
            if (errorData.message) {
              errorMessage = errorData.message;
            } else if (errorData.error) {
              errorMessage = errorData.error;
            }
          }
        } catch (parseError) {
          // 如果不是 JSON，使用原始文本（如果非空）
          if (errorText && errorText.trim()) {
            errorMessage = errorText;
          }
        }
      } catch (textError) {
        console.error("读取错误响应失败:", textError);
      }
      
      // 创建一个自定义错误对象，包含解析后的错误信息
      const error = new Error(errorMessage);
      (error as any).response = response;
      throw error;
    }

    const responseData = await response.json();
    // console.debug("API响应数据:", responseData);

    // 如果后端响应已经是标准格式 { success, data, message }，直接返回
    if (responseData && typeof responseData === "object" && "success" in responseData) {
      return responseData;
    }

    // 否则包装成标准格式
    return { success: true, data: responseData, message: "操作成功" };
  } catch (error) {
    console.error("API请求失败:", error);
    if (error instanceof TypeError && error.message.includes("Failed to fetch")) {
      message.error("网络连接失败，请检查服务器是否运行");
    } else {
      message.error(error instanceof Error ? error.message : "请求失败");
    }
    throw error;
  }
};

// 角色API
export const roleApi = {
  // 获取角色列表
  getRoleList: async (params: PaginationParams = {}) => {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        searchParams.append(key, String(value));
      }
    });

    return request(`/role/list?${searchParams.toString()}`);
  },

  // 获取角色详情
  getRoleDetail: async (id: string) => {
    return request(`/role/${id}`);
  },

  // 创建角色
  createRole: async (params: CreateRoleParams) => {
    return request("/role/add", {
      method: "POST",
      body: JSON.stringify(params),
    });
  },

  // 更新角色
  updateRole: async (id: string, params: UpdateRoleParams) => {
    return request(`/role/${id}`, {
      method: "PUT",
      body: JSON.stringify(params),
    });
  },

  // 删除角色
  deleteRole: async (id: string) => {
    return request(`/role/${id}`, {
      method: "DELETE",
    });
  },

  // 获取所有管理员列表（用于角色分配）
  getAllAdmins: async () => {
    return request("/role/admins");
  },

  // 获取角色的管理员列表
  getRoleAdmins: async (roleId: string) => {
    return request(`/role/${roleId}/admins`);
  },

  // 为角色分配管理员
  assignAdminsToRole: async (roleId: string, params: AssignAdminsParams) => {
    return request(`/role/${roleId}/admins`, {
      method: "POST",
      body: JSON.stringify(params),
    });
  },

  // 移除角色的管理员
  removeAdminFromRole: async (roleId: string, adminId: string) => {
    return request(`/role/${roleId}/admins/${adminId}`, {
      method: "DELETE",
    });
  },

  // 批量删除角色
  batchDeleteRoles: async (ids: string[]) => {
    return request("/role/batch-delete", {
      method: "POST",
      body: JSON.stringify({ ids }),
    });
  },

  // 获取角色权限
  getRolePermissions: async (roleId: string) => {
    return request(`/role/${roleId}/permissions`, {
      method: "GET",
    });
  },
};

export type { ApiResponse, PaginationParams, CreateRoleParams, UpdateRoleParams, AssignAdminsParams };
